import { useState } from "react";
import { useTranslation } from "react-i18next";
import WinbackModal from "@components/Subscriptions/WinbackModal";
import cancelZuoraAthletePremiumSubscription from "@api/cancelZuoraAthletePremiumSubscription";
import cancelStripeSubscription from "@api/cancelStripeSubscription";
import renewZuoraSubscriptionWithWinbackCoupon from "@api/renewZuoraSubscriptionWithWinbackCoupon";
import renewStripeSubscriptionWithWinbackCoupon from "@api/renewStripeSubscriptionWithWinbackCoupon";
import { useSubscriptionContext } from "@context/SubscriptionContext";
import { useUserContext } from "@context/UserContext";
import { useNotificationContext } from "@context/NotificationContext";
import { Subscription } from "@appTypes/index";
import { SUBSCRIPTION_SOURCES } from "@constants/subscriptions";
import { navigateTo } from "@utils/navigate";
import { triggerHotjarEvent, identifyHotjarUser, HOTJAR_EVENTS } from "@utils/hotjar";
import { getUserTypeDisplayName } from "@utils/userType";
import useSegment from "@hooks/useSegment";
import { ANALYTICS_EVENTS, ANALYTICS_LOCATIONS } from "@appTypes/analytics";

const useHandleWinbackFlow = (subscription: Subscription) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState<string | null>(null);
  const { refreshSubscriptions, updateSubscriptionLocally } = useSubscriptionContext();
  const { user } = useUserContext();
  const { showSuccess, showError } = useNotificationContext();
  const { t } = useTranslation();
  const { trackEvent } = useSegment();
  const isStripe = subscription.source === SUBSCRIPTION_SOURCES.STRIPE;

  const handleWinbackModalOpen = () => {
    setIsOpen(true);
  };

  const handleWinbackModalRedeemOffer = async () => {
    trackEvent(
      ANALYTICS_EVENTS.FEATURE_ENGAGED,
      "Winback Modal - Renew Subscription button clicked",
      ANALYTICS_LOCATIONS.SUBSCRIPTIONS_LIST,
      subscription.subscriptionType
    );
    setIsRedeeming(true);
    setRedeemError(null);

    if (isStripe) {
      const response = await renewStripeSubscriptionWithWinbackCoupon();
      setIsRedeeming(false);
      if (!response.success) {
        showError(response.error || t("Error applying coupon"));
        setIsOpen(false);
        return;
      }
      setIsOpen(false);
      showSuccess(response.data?.message ?? "Success applying coupon");
      refreshSubscriptions();
      return;
    }

    const response = await renewZuoraSubscriptionWithWinbackCoupon();
    setIsRedeeming(false);
    if (!response.success) {
      setRedeemError(response.error);
      return;
    }
    setIsOpen(false);
    if (response.data?.redirectUrl) {
      navigateTo(response.data.redirectUrl);
      return;
    }
    refreshSubscriptions();
  };

  const handleWinbackModalXClose = () => {
    trackEvent(
      ANALYTICS_EVENTS.FEATURE_ENGAGED,
      "Winback Modal - X button clicked",
      ANALYTICS_LOCATIONS.SUBSCRIPTIONS_LIST,
      subscription.subscriptionType
    );
    setIsOpen(false);
  };

  const handleConfirmCancel = async () => {
    trackEvent(
      ANALYTICS_EVENTS.FEATURE_ENGAGED,
      "Winback Modal - Cancel Subscription button clicked",
      ANALYTICS_LOCATIONS.SUBSCRIPTIONS_LIST,
      subscription.subscriptionType
    );
    setIsCancelling(true);

    if (isStripe) {
      if (!subscription.subscriptionIdentifier) {
        setIsCancelling(false);
        showError(t("Error cancelling subscription. Please try again or contact Customer Support for help"));
        return;
      }
      const response = await cancelStripeSubscription(subscription.subscriptionIdentifier);
      setIsCancelling(false);
      if (!response.success) {
        showError(response.error);
        return;
      }
      const message = response.data?.message ?? t("Your subscription has been cancelled successfully");
      setIsOpen(false);
      updateSubscriptionLocally(subscription.subscriptionIdentifier, { autoRenew: false });
      showSuccess(message);
      return;
    }

    const response = await cancelZuoraAthletePremiumSubscription();
    setIsCancelling(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setIsOpen(false);
    if (subscription.subscriptionIdentifier) {
      updateSubscriptionLocally(subscription.subscriptionIdentifier, { autoRenew: false });
    }
    showSuccess(t("Your subscription has been cancelled successfully"));
    if (user?.personId) {
      identifyHotjarUser(user.personId, {
        acctExpireDate: user.expireDate,
        isCoached: user.settings?.account?.isCoached,
        acctType: getUserTypeDisplayName(user.userType),
      });
    }
    triggerHotjarEvent(HOTJAR_EVENTS.CANCEL_ACCOUNT);
  };

  const modal = (
    <WinbackModal
      isOpen={isOpen}
      onRedeem={handleWinbackModalRedeemOffer}
      onXClose={handleWinbackModalXClose}
      onConfirmCancel={handleConfirmCancel}
      isProcessing={isCancelling || isRedeeming}
      error={redeemError}
    />
  );
  return { handleWinbackModalOpen, modal };
};

export default useHandleWinbackFlow;
